import { createApp } from "vue";
import { logger } from "@/services/logger/logger.service";
import { createPinia } from "pinia";
import PrimeVue from "primevue/config";
import ToastService from "primevue/toastservice";
import ConfirmationService from "primevue/confirmationservice";
import Tooltip from "primevue/tooltip";
import Aura from "@primevue/themes/aura";

// Import PrimeIcons
import "primeicons/primeicons.css";

import App from "./App.vue";
import router from "./router";
import { setupSentry } from "@/plugins/sentry";
import { configService } from "@/services/config.service";
import { useFingerprint } from "@/composables/useFingerprint";
import { axiomService } from "@/services/axiom/axiom.service";

// Import global styles
import "@/assets/css/main.css";

// Initialize configuration before creating the app
async function initializeApp() {
  try {
    // Load runtime configuration
    await configService.initialize();

    // Initialize device fingerprint early
    const fingerprint = useFingerprint();
    await fingerprint.initFingerprint();
    logger.info("Device fingerprint initialized", {
      deviceId: fingerprint.visitorId.value,
    });

    // Initialize Axiom analytics
    await axiomService.initialize();

    const app = createApp(App);

    // Initialize Sentry with runtime config
    setupSentry(app, router);

    // Pinia store
    app.use(createPinia());

    // Vue Router
    app.use(router);

    // PrimeVue configuration
    app.use(PrimeVue, {
      theme: {
        preset: Aura,
        options: {
          prefix: "p",
          darkModeSelector: ".dark",
          cssLayer: false,
        },
      },
    });

    // PrimeVue services and directives
    app.use(ToastService);
    app.use(ConfirmationService);
    app.directive("tooltip", Tooltip);

    app.mount("#app");
  } catch (error) {
    logger.error("Failed to initialize application:", error);
    // Show error page or redirect
    document.body.innerHTML = `
      <div style="display: flex; justify-content: center; align-items: center; height: 100vh; font-family: Arial, sans-serif;">
        <div style="text-align: center;">
          <h1>Configuration Error</h1>
          <p>Failed to load application configuration.</p>
          <p style="color: #666; font-size: 14px;">${error instanceof Error ? error.message : "Unknown error"}</p>
        </div>
      </div>
    `;
  }
}

// Start the application
initializeApp();
